import React, { useState, useEffect, useRef, useMemo, useCallback } from 'react';
import { ArrowLeft, ChevronDown, Trash2, X } from 'lucide-react';
import { softDeleteAssignmentSession, updateAssignmentMetadata } from '../services/firebaseService';
import { auth } from '../firebase';
import { useAuth } from '../contexts/AuthContext';
import { usePublishAccess } from '../contexts/PublishAccessContext';
import { useTimeFilter } from '../contexts/TimeFilterContext';
import { useSchedulerStatus } from '../contexts/SchedulerStatusContext';
import { schedulerApiFetch } from '../api/schedulerClient';
import { buildAssignLinesOrderPayload, buildPortalPlanPayload } from '../utils/portalPlanPublish';
import { planDateIso } from '../utils/groupLeg';
import { useMultiloadRoutes, usePortalAssignment, useTripsAssignment } from '../queries/useAssignmentChunks';
import {
  SESSION_LIST_DEFAULT_LIMIT,
  SESSION_LIST_MAX_LIMIT,
  clampSessionListLimit,
  useInvalidateSessionList,
  useRefreshSessionList,
  useSessionList,
} from '../queries/useSessionList';
import { useUserConfig, getTimezoneFromUserConfig } from '../queries/useUserConfig';

const TimeFilter = ({ headerClock }) => {
  const { loading: authLoading, user } = useAuth();
  const { publishSelectionUnlocked } = usePublishAccess();
  const { schedulerStatus } = useSchedulerStatus();
  const {
    selectedResultId,
    handleResultSelect: onResultSelect,
    firePublishSuccess: onPublishSuccess,
    patchAssignmentSessionCache,
    invalidateAssignmentSession,
  } = useTimeFilter();

  const [limitCount, setLimitCount] = useState(SESSION_LIST_DEFAULT_LIMIT);
  const listLimit = clampSessionListLimit(limitCount);
  const invalidateSessionList = useInvalidateSessionList();
  const refreshSessionList = useRefreshSessionList();
  const [sessionMenuOpen, setSessionMenuOpen] = useState(false);
  const [deleteConfirm, setDeleteConfirm] = useState(null);
  const [isDeletingSession, setIsDeletingSession] = useState(false);
  const [deleteSessionError, setDeleteSessionError] = useState(null);
  const sessionMenuRef = useRef(null);

  // Company timezone from user-config only (same source as header). No LA / server fallback.
  const { data: userConfigData } = useUserConfig({ enabled: !authLoading && Boolean(user) });
  const companyTimezone = getTimezoneFromUserConfig(userConfigData?.config);

  const {
    data: sessionListData,
    isLoading: listLoading,
    isFetching: listFetching,
    error: listQueryError,
  } = useSessionList(listLimit, { enabled: !authLoading && Boolean(user) });

  const [loadError, setLoadError] = useState(null);
  const [showPublishPopup, setShowPublishPopup] = useState(false);
  const [publishStep, setPublishStep] = useState('choose');
  const [publishChoice, setPublishChoice] = useState(null);
  const [isPublishing, setIsPublishing] = useState(false);
  const [publishError, setPublishError] = useState(null);
  const [publishSuccess, setPublishSuccess] = useState(null);
  const [filteredPayloadData, setFilteredPayloadData] = useState([]);
  const [driverGroups, setDriverGroups] = useState([]);
  const [selectedDriverKeys, setSelectedDriverKeys] = useState(new Set());
  const [cachedAuthToken, setCachedAuthToken] = useState(null);

  const publishNeedsData = showPublishPopup && Boolean(selectedResultId);
  const tripsForPublish = useTripsAssignment(selectedResultId, {
    enabled: publishNeedsData,
  });
  const multiloadForPublish = useMultiloadRoutes(selectedResultId, {
    enabled: publishNeedsData && publishChoice !== 'portal',
  });
  const portalForPublish = usePortalAssignment(selectedResultId, {
    enabled: publishNeedsData && publishChoice === 'portal',
  });

  const publishAssignmentData = useMemo(() => {
    const t = tripsForPublish.normalized;
    const p = portalForPublish.normalized;
    if (!t && !p) return null;
    const multiload_routes =
      multiloadForPublish.chunkMap?.multiload_routes ||
      t?.multiload_routes ||
      {};
    if (!t) return p;
    if (!p?.original_plan) return { ...t, multiload_routes };
    return { ...t, original_plan: p.original_plan, multiload_routes };
  }, [tripsForPublish.normalized, portalForPublish.normalized, multiloadForPublish.chunkMap]);

  const loading = authLoading || (listLoading && !sessionListData);
  const listSyncing = listFetching && Boolean(sessionListData);

  const availableResults = useMemo(() => {
    if (sessionListData?.length) return sessionListData;
    if (listQueryError?.message === 'USER_NOT_AUTHENTICATED') return [];
    if (listQueryError?.message === 'NO_USER_DATA_RUN_PLANNER_FIRST') return [];
    if (listQueryError) {
      return [
        {
          id: 'mock-session-error',
          timestamp: new Date().toISOString(),
          results: { summary: { total_trips: 100, assigned_trips: 80, unassigned_trips: 20, active_drivers: 15 } },
        },
      ];
    }
    return [];
  }, [sessionListData, listQueryError]);

  useEffect(() => {
    if (!authLoading && !user) {
      setLoadError('Please sign in to view sessions');
      return;
    }
    if (listQueryError) {
      if (listQueryError.message === 'USER_NOT_AUTHENTICATED') {
        setLoadError('Please sign in to view sessions');
      } else if (listQueryError.message === 'NO_USER_DATA_RUN_PLANNER_FIRST') {
        setLoadError('No sessions found. Run planner first.');
      } else {
        setLoadError('Failed to load timestamps');
      }
      return;
    }
    setLoadError(null);
  }, [authLoading, user, listQueryError]);

  const schedulerFingerprint = useMemo(() => {
    if (!schedulerStatus) return null;
    return `${schedulerStatus.status}|${schedulerStatus.is_running}|${schedulerStatus.run_count}`;
  }, [schedulerStatus]);

  const prevSchedulerFpRef = useRef(null);
  useEffect(() => {
    if (authLoading || !user || schedulerFingerprint == null) return;
    const prev = prevSchedulerFpRef.current;
    prevSchedulerFpRef.current = schedulerFingerprint;
    if (prev === null) return;
    if (prev === schedulerFingerprint) return;
    invalidateSessionList(listLimit);
  }, [authLoading, user, schedulerFingerprint, invalidateSessionList, listLimit]);

  const handleRetryList = () => {
    refreshSessionList(listLimit);
  };

  useEffect(() => {
    if (!sessionMenuOpen) return undefined;
    const onPointerDown = (event) => {
      if (sessionMenuRef.current && !sessionMenuRef.current.contains(event.target)) {
        setSessionMenuOpen(false);
      }
    };
    const onKeyDown = (event) => {
      if (event.key === 'Escape') setSessionMenuOpen(false);
    };
    document.addEventListener('mousedown', onPointerDown);
    document.addEventListener('keydown', onKeyDown);
    return () => {
      document.removeEventListener('mousedown', onPointerDown);
      document.removeEventListener('keydown', onKeyDown);
    };
  }, [sessionMenuOpen]);

  const formatTimestamp = useCallback((timestamp) => {
    try {
      // Wait for user-config company timezone — never fall back to LA / browser local.
      if (!companyTimezone) return '…';

      let date;
      if (timestamp instanceof Date) {
        date = timestamp;
      } else if (typeof timestamp === 'string') {
        let raw = timestamp.trim();
        // DB sends UTC; naive ISO strings (no Z/offset) must not be treated as browser local.
        const hasZone = /(?:Z|[+-]\d{2}:?\d{2})$/i.test(raw) || /\bGMT\b/i.test(raw);
        if (!hasZone && /^\d{4}-\d{2}-\d{2}T\d{2}:\d{2}/.test(raw)) {
          raw = `${raw}Z`;
        }
        date = new Date(raw);
        if (isNaN(date.getTime())) date = new Date(raw.replace(' GMT', ' +0000'));
      } else if (timestamp && typeof timestamp === 'object' && timestamp.toDate) {
        date = timestamp.toDate();
      } else {
        return 'Invalid Date';
      }
      if (isNaN(date.getTime())) return 'Invalid Date';

      // UTC session → company timezone from user-config only.
      const parts = new Intl.DateTimeFormat('en-US', {
        timeZone: companyTimezone,
        month: 'short',
        day: 'numeric',
        year: 'numeric',
        hour: '2-digit',
        minute: '2-digit',
        second: '2-digit',
        hour12: false,
      }).formatToParts(date);
      const get = (type) => parts.find((p) => p.type === type)?.value || '';
      return `${get('month')} ${get('day')} ${get('year')} ${get('hour')}:${get('minute')}:${get('second')}`;
    } catch (error) {
      console.error('Error formatting timestamp:', timestamp, error);
      return 'Invalid Date';
    }
  }, [companyTimezone]);

  const requestDeleteSession = useCallback((result, index, event) => {
    event?.preventDefault?.();
    event?.stopPropagation?.();
    if (!result?.id || String(result.id).startsWith('mock-')) return;
    setDeleteSessionError(null);
    setDeleteConfirm({
      id: result.id,
      label: `Session ${index + 1} - ${formatTimestamp(result.timestamp)}`,
    });
    setSessionMenuOpen(false);
  }, [formatTimestamp]);

  const cancelDeleteSession = useCallback(() => {
    if (isDeletingSession) return;
    setDeleteConfirm(null);
    setDeleteSessionError(null);
  }, [isDeletingSession]);

  const confirmDeleteSession = useCallback(async () => {
    if (!deleteConfirm?.id || isDeletingSession) return;
    const deletedId = deleteConfirm.id;
    setIsDeletingSession(true);
    setDeleteSessionError(null);
    try {
      await softDeleteAssignmentSession(deletedId);
      invalidateAssignmentSession(deletedId);

      const remaining = availableResults.filter((r) => r.id !== deletedId);
      if (selectedResultId === deletedId) {
        onResultSelect(remaining[0]?.id || null);
      }

      setDeleteConfirm(null);
      await refreshSessionList(listLimit);
    } catch (err) {
      console.error('Failed to delete session:', err);
      setDeleteSessionError(err?.message || 'Failed to delete session');
    } finally {
      setIsDeletingSession(false);
    }
  }, [
    deleteConfirm,
    isDeletingSession,
    availableResults,
    selectedResultId,
    onResultSelect,
    invalidateAssignmentSession,
    refreshSessionList,
    listLimit,
  ]);

  const getAuthToken = async () => {
    const user = auth.currentUser;
    if (user) return await user.getIdToken();
    return null;
  };

  const openPublishFlow = () => {
    setPublishError(null);
    setPublishSuccess(null);
    if (!selectedResultId) { setPublishError('Please select a planning session first.'); return; }
    if (!publishAssignmentData) { setPublishError('No assignment data available.'); return; }
    const timezone = publishAssignmentData.metadata?.timezone;
    if (!timezone) { setPublishError('Cannot publish: session timezone is missing.'); return; }
    const allTrips = [];
    if (publishAssignmentData.drivers) {
      publishAssignmentData.drivers.forEach((d) => {
        (d.trips || []).forEach((t) => { if (!t.is_busy_trip && !t.is_break) allTrips.push(t); });
      });
    }
    if (publishAssignmentData.unassigned_trips) allTrips.push(...publishAssignmentData.unassigned_trips);
    const op = publishAssignmentData.original_plan;
    if (op) {
      (op.drivers || []).forEach((b) => (b.trips || []).forEach((t) => allTrips.push(t)));
      if (op.unassigned_trips) allTrips.push(...op.unassigned_trips);
    }
    const todayInTz = new Date().toLocaleDateString('en-CA', { timeZone: timezone });
    for (const trip of allTrips) {
      const scheduleIso = planDateIso(trip);
      if (!scheduleIso) continue;
      const tripDateInTz = new Date(scheduleIso).toLocaleDateString('en-CA', { timeZone: timezone });
      if (tripDateInTz < todayInTz) { setPublishError('Past date plan cannot be published.'); return; }
    }
    setPublishStep('choose');
    setPublishChoice(null);
    setShowPublishPopup(true);
  };

  const handlePublishClick = () => {
    openPublishFlow();
  };

  const buildPlannerPayload = () => {
    const payload = { data: [] };
    const { drivers, unassigned_trips } = publishAssignmentData;
    drivers.forEach((driver) => {
      if (driver.trips && driver.trips.length > 0) {
        driver.trips.forEach((trip, index) => {
          if (trip.is_busy_trip || trip.is_break) return;
          payload.data.push({
            selectedTrips: [trip.id], status: 'offered', priority: index + 1, assignThrough: 'planner',
            driver: { _id: driver.driver_id, displayName: driver.driver_name, profileImageURL: driver.profileImageURL || '' }
          });
        });
      }
    });
    if (unassigned_trips && unassigned_trips.length > 0) {
      payload.data.push({ selectedTrips: unassigned_trips.map(t => t.id), status: 'unassigned', priority: 0, assignThrough: 'planner' });
    }
    return payload;
  };

  const handlePublishConfirm = async () => {
    const publishType = publishChoice || 'planner';
    try {
      setIsPublishing(true);
      setPublishError(null);
      if (!publishAssignmentData) throw new Error('No assignment data available.');
      let payload = publishType === 'portal' ? buildPortalPlanPayload(publishAssignmentData) : buildPlannerPayload();
      if (publishType === 'planner' && (!publishAssignmentData.drivers || publishAssignmentData.drivers.length === 0)) {
        throw new Error('No driver assignments found in selected session');
      }
      if (payload.data.length === 0) throw new Error('No trips to publish.');
      const authToken = await getAuthToken();
      if (!authToken) throw new Error('Authentication required. Please sign in again.');

      const allIds = [];
      payload.data.forEach((item) => {
        (item.selectedTrips || []).forEach((id) => { const sid = String(id); if (sid && !allIds.includes(sid)) allIds.push(sid); });
      });
      if (allIds.length === 0) throw new Error('No trip IDs to validate.');

      const validateResponse = await schedulerApiFetch('validate', { method: 'POST', bearerToken: authToken, body: { _ids: allIds } });
      if (!validateResponse.ok) {
        const errorData = await validateResponse.json().catch(() => ({}));
        throw new Error(`Validation failed: ${errorData.detail || validateResponse.statusText}`);
      }
      const validateResult = await validateResponse.json();
      const validatedIds = (validateResult.validTripIds || validateResult._ids || []).map((id) => String(id));
      const validSet = new Set(validatedIds);

      const filtered = [];
      payload.data.forEach((item) => {
        const trips = (item.selectedTrips || []).filter((id) => validSet.has(String(id)));
        if (trips.length === 0) return;
        filtered.push({ ...item, selectedTrips: trips });
      });
      if (filtered.length === 0) throw new Error('No trips to publish after validation.');

      if (!publishSelectionUnlocked) {
        const allKeys = new Set(
          filtered.map((item) => (
            item.status === 'unassigned' ? '__unassigned__' : (item.driver?._id ?? 'no-driver')
          ))
        );
        await executePublish(filtered, allKeys, authToken, publishType);
        return;
      }

      const groups = new Map();
      filtered.forEach((item) => {
        const key = item.status === 'unassigned' ? '__unassigned__' : (item.driver?._id ?? 'no-driver');
        if (!groups.has(key)) {
          groups.set(key, {
            key,
            name: item.status === 'unassigned' ? 'Unassigned' : (item.driver?.displayName || 'Unknown Driver'),
            profileImage: item.status === 'unassigned' ? null : (item.driver?.profileImageURL || null),
            tripCount: 0,
            items: [],
          });
        }
        const g = groups.get(key);
        g.tripCount += (item.selectedTrips || []).length;
        g.items.push(item);
      });

      const groupsArr = Array.from(groups.values()).sort((a, b) => {
        if (a.key === '__unassigned__') return 1;
        if (b.key === '__unassigned__') return -1;
        return a.name.localeCompare(b.name);
      });

      setFilteredPayloadData(filtered);
      setDriverGroups(groupsArr);
      setSelectedDriverKeys(new Set(groupsArr.map((g) => g.key)));
      setCachedAuthToken(authToken);
      setPublishStep('select-drivers');
    } catch (err) {
      console.error('Error during publish flow:', err);
      const prefix = publishSelectionUnlocked ? 'Failed to validate' : 'Failed to publish';
      setPublishError(`${prefix}: ${err.message}`);
    } finally {
      setIsPublishing(false);
    }
  };

  const resetPublishPopupState = useCallback(() => {
    setShowPublishPopup(false);
    setPublishStep('choose');
    setPublishChoice(null);
    setFilteredPayloadData([]);
    setDriverGroups([]);
    setSelectedDriverKeys(new Set());
    setCachedAuthToken(null);
  }, []);

  const executePublish = useCallback(async (filteredData, selectedKeys, authToken, publishType) => {
    const selected = filteredData.filter((item) => {
      const key = item.status === 'unassigned' ? '__unassigned__' : (item.driver?._id ?? 'no-driver');
      return selectedKeys.has(key);
    });
    if (selected.length === 0) throw new Error('No drivers selected to publish.');

    // Assignment payload (offers + unassigned) — required for portal/planner to update NEMT.
    const byDriver = new Map();
    selected.forEach((item) => {
      if (item.status === 'unassigned') { item.priority = 0; return; }
      const driverKey = item.driver?._id ?? 'no-driver';
      if (!byDriver.has(driverKey)) byDriver.set(driverKey, []);
      byDriver.get(driverKey).push(item);
    });
    byDriver.forEach((items) => { items.forEach((item, idx) => { item.priority = idx + 1; }); });

    const payload = { data: selected };
    // Route order payload — normal sequential, or multiload route when available.
    const lineOrderPayload = buildAssignLinesOrderPayload(publishAssignmentData, selected, { publishType });

    const publishRequest = schedulerApiFetch('publish', { method: 'POST', bearerToken: authToken, body: payload });
    const lineOrderRequest = lineOrderPayload.data.length > 0
      ? schedulerApiFetch('publish-line-order', { method: 'POST', bearerToken: authToken, body: lineOrderPayload })
      : Promise.resolve({ ok: true });
    const [publishResult, lineOrderResult] = await Promise.allSettled([publishRequest, lineOrderRequest]);

    if (publishResult.status === 'rejected') {
      throw new Error(`Publishing failed: ${publishResult.reason?.message || publishResult.reason || 'Network error'}`);
    }

    const publishResponse = publishResult.value;
    if (!publishResponse.ok) {
      const errorData = await publishResponse.json().catch(() => ({}));
      throw new Error(`Publishing failed: ${errorData.detail || publishResponse.statusText}`);
    }

    let routePublishFailed = false;
    if (lineOrderResult.status === 'rejected') {
      routePublishFailed = true;
    } else if (!lineOrderResult.value.ok) {
      routePublishFailed = true;
    }

    const updateField = publishType === 'portal' ? { portalPlanPublished: true } : { isPublished: true };
    updateField.routePublishFailed = routePublishFailed;
    updateField.routePublishFailedAt = routePublishFailed ? new Date().toISOString() : null;
    await updateAssignmentMetadata(selectedResultId, updateField);
    patchAssignmentSessionCache(selectedResultId, { publishFlags: updateField });
    await invalidateSessionList(listLimit);
    setPublishError(null);
    setPublishSuccess(
      `Successfully published ${publishType === 'portal' ? 'Portal' : 'Planner'} plan (${payload.data.length} items)!${
        routePublishFailed ? ' Route order publish failed.' : ''
      }`
    );
    resetPublishPopupState();
    if (onPublishSuccess) onPublishSuccess(publishType);
  }, [
    publishAssignmentData,
    selectedResultId,
    invalidateSessionList,
    listLimit,
    patchAssignmentSessionCache,
    onPublishSuccess,
    resetPublishPopupState,
  ]);

  const handleFinalPublish = async () => {
    const publishType = publishChoice || 'planner';
    try {
      setIsPublishing(true);
      setPublishError(null);

      const authToken = cachedAuthToken || await getAuthToken();
      if (!authToken) throw new Error('Authentication required. Please sign in again.');

      await executePublish(filteredPayloadData, selectedDriverKeys, authToken, publishType);
    } catch (err) {
      console.error('Error publishing session:', err);
      setPublishError(`Failed to publish: ${err.message}`);
    } finally {
      setIsPublishing(false);
    }
  };

  const handlePublishChoice = (choice) => { setPublishChoice(choice); setPublishStep('confirm'); };
  const handlePublishCancel = () => {
    resetPublishPopupState();
  };
  const handleConfirmBack = () => { setPublishStep('choose'); setPublishChoice(null); };
  const handleSelectDriversBack = () => { setPublishStep('confirm'); };
  const toggleDriverKey = (key) => {
    setSelectedDriverKeys((prev) => {
      const next = new Set(prev);
      if (next.has(key)) next.delete(key); else next.add(key);
      return next;
    });
  };
  const toggleAllDrivers = () => {
    setSelectedDriverKeys((prev) => {
      if (prev.size === driverGroups.length) return new Set();
      return new Set(driverGroups.map((g) => g.key));
    });
  };

  /* ─── Published labels ───
   * Prefer session list metadata; fall back to loaded assignment payload.
   */
  const selectedSessionMeta = useMemo(() => {
    if (!selectedResultId) return null;
    return availableResults.find((r) => r.id === selectedResultId) || null;
  }, [availableResults, selectedResultId]);

  // Use OR (not ??) so a stale `false` on one source doesn't hide a `true` from the other source.
  // This happens on some pages that pass `results` objects into context which may carry default `false` flags.
  const isPlannerPublished = Boolean(selectedSessionMeta?.isPublished || publishAssignmentData?.isPublished);
  const isPortalPublished = Boolean(selectedSessionMeta?.portalPlanPublished || publishAssignmentData?.portalPlanPublished);
  const routePublishFailed = Boolean(selectedSessionMeta?.routePublishFailed || publishAssignmentData?.routePublishFailed);
  const publishedLabels = selectedResultId && (isPlannerPublished || isPortalPublished || routePublishFailed);

  /* ─── Render ─── */
  return (
    <>
      <div className="flex items-center justify-between gap-4 flex-1">
        {/* Left: session selector */}
        <div className="flex items-center gap-3 min-w-0">
            {(authLoading || loading) ? (
              <div className="flex items-center gap-2">
                <div className="animate-spin rounded-full h-3.5 w-3.5 border-b-2 border-main-primary" />
                <span className="text-xs text-main-light">{authLoading ? 'Authenticating...' : 'Loading...'}</span>
              </div>
            ) : loadError ? (
              <div className="flex items-center gap-2">
                <span className="text-xs text-main-danger">{loadError}</span>
                <button type="button" onClick={handleRetryList} className="text-xs text-main-primary hover:underline font-medium">Retry</button>
              </div>
            ) : (
              <>
                <div className="flex items-center gap-1.5">
                  <label id="timestamp-select-label" className="text-xs font-medium text-main-light whitespace-nowrap">Session:</label>
                  {listSyncing && (
                    <span
                      className="inline-flex h-3.5 w-3.5 shrink-0 rounded-full border-2 border-main-primary/30 border-t-main-primary animate-spin"
                      title="Updating sessions…"
                      aria-hidden
                    />
                  )}
                  <div className="relative" ref={sessionMenuRef}>
                    <button
                      type="button"
                      id="timestamp-select"
                      aria-labelledby="timestamp-select-label"
                      aria-haspopup="listbox"
                      aria-expanded={sessionMenuOpen}
                      onClick={() => setSessionMenuOpen((open) => !open)}
                      className="w-72 flex items-center justify-between gap-2 rounded-md text-main-primary border border-main-base bg-white shadow-sm focus:border-main-primary focus:ring-1 focus:ring-main-primary text-xs py-1.5 px-2 text-left"
                    >
                      <span className="truncate">
                        {selectedSessionMeta
                          ? `Session ${availableResults.findIndex((r) => r.id === selectedResultId) + 1} - ${formatTimestamp(selectedSessionMeta.timestamp)}`
                          : 'Select a planning session...'}
                      </span>
                      <ChevronDown className={`h-3.5 w-3.5 shrink-0 text-main-light transition-transform ${sessionMenuOpen ? 'rotate-180' : ''}`} />
                    </button>
                    {sessionMenuOpen && (
                      <ul
                        role="listbox"
                        aria-labelledby="timestamp-select-label"
                        className="absolute z-50 mt-1 w-80 max-h-72 overflow-auto rounded-md border border-main-base bg-white shadow-lg py-1"
                      >
                        {availableResults.length === 0 ? (
                          <li className="px-3 py-2 text-xs text-main-light">No sessions available</li>
                        ) : (
                          availableResults.map((result, index) => {
                            const selected = result.id === selectedResultId;
                            const canDelete = !String(result.id).startsWith('mock-');
                            return (
                              <li
                                key={result.id}
                                role="option"
                                aria-selected={selected}
                                className={`flex items-center gap-1 px-1.5 py-0.5 ${selected ? 'bg-main-primary/5' : ''}`}
                              >
                                <button
                                  type="button"
                                  className={`flex-1 min-w-0 text-left rounded px-2 py-1.5 text-xs truncate ${
                                    selected ? 'text-main-primary font-medium' : 'text-main-primary hover:bg-gray-50'
                                  }`}
                                  onClick={() => {
                                    onResultSelect(result.id);
                                    setSessionMenuOpen(false);
                                  }}
                                >
                                  Session {index + 1} - {formatTimestamp(result.timestamp)}
                                </button>
                                {canDelete && (
                                  <button
                                    type="button"
                                    title="Delete session"
                                    aria-label={`Delete session ${index + 1}`}
                                    className="shrink-0 p-1.5 rounded text-main-light hover:text-red-600 hover:bg-red-50"
                                    onClick={(event) => requestDeleteSession(result, index, event)}
                                  >
                                    <Trash2 className="h-3.5 w-3.5" />
                                  </button>
                                )}
                              </li>
                            );
                          })
                        )}
                      </ul>
                    )}
                  </div>
                </div>

                <div className="hidden sm:flex items-center gap-1.5">
                  <label htmlFor="limit-count" className="text-xs font-medium text-main-light whitespace-nowrap">Limit:</label>
                  <input
                    id="limit-count"
                    type="number"
                    min={1}
                    max={SESSION_LIST_MAX_LIMIT}
                    value={limitCount}
                    onChange={(e) => {
                      const v = e.target.value;
                      if (v === '') { setLimitCount(''); return; }
                      const n = parseInt(v, 10);
                      if (!isNaN(n) && n >= 1) setLimitCount(n);
                    }}
                    onBlur={() => {
                      if (limitCount === '' || limitCount < 1) {
                        setLimitCount(SESSION_LIST_DEFAULT_LIMIT);
                      } else {
                        setLimitCount(clampSessionListLimit(limitCount));
                      }
                    }}
                    onKeyDown={(e) => {
                      if (e.key === 'Enter') {
                        e.currentTarget.blur();
                      }
                    }}
                    className="w-14 !py-1.5 !text-xs"
                    title={`Number of sessions to load (1–${SESSION_LIST_MAX_LIMIT})`}
                  />
                </div>

                {publishedLabels && (
                  <div className="flex items-center gap-2 ml-1 flex-wrap">
                    {isPlannerPublished && (
                      <span className="inline-flex items-center gap-1 px-2 py-0.5 rounded-full bg-green-50 text-green-700 text-[10px] font-medium ring-1 ring-green-200">
                        <span className="w-1.5 h-1.5 rounded-full bg-green-500" />
                        Planner published
                      </span>
                    )}
                    {isPortalPublished && (
                      <span className="inline-flex items-center gap-1 px-2 py-0.5 rounded-full bg-blue-50 text-blue-700 text-[10px] font-medium ring-1 ring-blue-200">
                        <span className="w-1.5 h-1.5 rounded-full bg-blue-500" />
                        Portal published
                      </span>
                    )}
                    {routePublishFailed && (
                      <span className="inline-flex items-center gap-1 px-2 py-0.5 rounded-full bg-amber-50 text-amber-700 text-[10px] font-medium ring-1 ring-amber-200">
                        <span className="w-1.5 h-1.5 rounded-full bg-amber-500" />
                        Route publish failed
                      </span>
                    )}
                  </div>
                )}
              </>
            )}
          </div>

          {/* Right: actions */}
          {!authLoading && !loading && !loadError && (
            <div className="flex items-center gap-4 flex-shrink-0">
              {headerClock && (
                <div className="flex-shrink-0 mr-2">
                  {headerClock}
                </div>
              )}
              <div className="flex items-center gap-2">
                <button
                  onClick={() => onResultSelect('')}
                  disabled={!selectedResultId}
                  className="px-3 py-1.5 text-xs font-medium rounded-md border border-gray-200 text-main-dark bg-white hover:bg-gray-50 disabled:opacity-40 disabled:cursor-not-allowed transition-colors"
                >
                  Clear
                </button>
                <button
                  onClick={handlePublishClick}
                  disabled={!selectedResultId}
                  className="px-3 py-1.5 text-xs font-medium rounded-md bg-main-primary text-white hover:bg-main-primary/90 disabled:opacity-40 disabled:cursor-not-allowed transition-colors"
                >
                  Publish
                </button>
              </div>
            </div>
          )}
        </div>

      {/* Soft-delete session confirm */}
      {deleteConfirm && (
        <>
          <div className="fixed inset-0 bg-black/50 z-50" onClick={cancelDeleteSession} />
          <div
            role="dialog"
            aria-modal="true"
            aria-labelledby="delete-session-title"
            className="fixed top-1/2 left-1/2 -translate-x-1/2 -translate-y-1/2 z-[60] bg-white rounded-2xl shadow-2xl border border-main-base w-full max-w-md overflow-hidden"
          >
            <div className="px-6 py-4 border-b border-main-base">
              <h3 id="delete-session-title" className="text-lg font-bold text-main-primary">Delete session?</h3>
            </div>
            <div className="px-6 py-6 space-y-2">
              <p className="text-sm text-main-secondary">
                Are you sure you want to delete this{' '}
                <span className="font-medium text-main-primary">({deleteConfirm.label})</span>?
              </p>
              {deleteSessionError && (
                <p className="text-sm text-main-danger">{deleteSessionError}</p>
              )}
            </div>
            <div className="px-6 py-4 bg-main-base-50 border-t border-main-base flex justify-end gap-2">
              <button
                type="button"
                onClick={cancelDeleteSession}
                disabled={isDeletingSession}
                className="px-3 py-1.5 text-xs font-medium rounded-md border border-main-base text-main-primary hover:bg-white disabled:opacity-40"
              >
                Cancel
              </button>
              <button
                type="button"
                onClick={confirmDeleteSession}
                disabled={isDeletingSession}
                className="px-3 py-1.5 text-xs font-medium rounded-md bg-red-600 text-white hover:bg-red-700 disabled:opacity-40 disabled:cursor-not-allowed"
              >
                {isDeletingSession ? 'Deleting…' : 'Delete'}
              </button>
            </div>
          </div>
        </>
      )}

      {/* Publish Error Popup */}
      {publishError && (
        <>
          <div className="fixed inset-0 bg-black/50 z-50" onClick={() => setPublishError(null)} />
          <div className="fixed top-1/2 left-1/2 -translate-x-1/2 -translate-y-1/2 z-[60] bg-white rounded-2xl shadow-2xl border border-red-200 w-full max-w-md overflow-hidden">
            <div className="px-6 py-4 bg-gradient-to-r from-red-50 to-white border-b border-red-100">
              <h3 className="text-lg font-bold text-red-700">Publish Failed</h3>
            </div>
            <div className="px-6 py-6">
              <p className="text-main-secondary">{publishError}</p>
            </div>
            <div className="px-6 py-4 bg-main-base-50 border-t border-main-base flex justify-end">
              <button onClick={() => setPublishError(null)} className="btn-primary">OK</button>
            </div>
          </div>
        </>
      )}

      {/* Publish Success Popup */}
      {publishSuccess && (
        <>
          <div className="fixed inset-0 bg-black/50 z-50" onClick={() => setPublishSuccess(null)} />
          <div className="fixed top-1/2 left-1/2 -translate-x-1/2 -translate-y-1/2 z-[60] bg-white rounded-2xl shadow-2xl border border-emerald-200 w-full max-w-md overflow-hidden">
            <div className="px-6 py-4 bg-gradient-to-r from-emerald-50 to-white border-b border-emerald-100">
              <h3 className="text-lg font-bold text-emerald-700">Success</h3>
            </div>
            <div className="px-6 py-6">
              <p className="text-main-secondary">{publishSuccess}</p>
            </div>
            <div className="px-6 py-4 bg-main-base-50 border-t border-main-base flex justify-end">
              <button onClick={() => setPublishSuccess(null)} className="btn-primary">OK</button>
            </div>
          </div>
        </>
      )}

      {/* Publish Popup: choose → confirm → select-drivers */}
      {showPublishPopup && (() => {
        const selResult = selectedResultId ? availableResults.find((r) => r.id === selectedResultId) : null;
        const selIdx = selResult ? availableResults.findIndex((r) => r.id === selectedResultId) : -1;
        const sessionLabel = selResult ? `Session ${selIdx + 1} · ${formatTimestamp(selResult.timestamp)}` : null;
        const stepTitles = {
          choose: 'What do you want to publish?',
          confirm: 'Confirm publication',
          'select-drivers': 'Select drivers to publish',
        };
        const totalSelectedTrips = driverGroups.reduce((sum, g) => sum + (selectedDriverKeys.has(g.key) ? g.tripCount : 0), 0);
        const steps = publishSelectionUnlocked
          ? [
            { key: 'choose', label: 'Choose' },
            { key: 'confirm', label: 'Confirm' },
            { key: 'select-drivers', label: 'Drivers' },
          ]
          : [
            { key: 'choose', label: 'Choose' },
            { key: 'confirm', label: 'Confirm' },
          ];

        return (
          <>
            <div className="fixed inset-0 bg-black/50 z-50 transition-opacity" onClick={handlePublishCancel} />
            <div className="fixed top-1/2 left-1/2 -translate-x-1/2 -translate-y-1/2 z-[60] bg-white rounded-2xl shadow-2xl border border-gray-200 w-full max-w-lg max-h-[85vh] flex flex-col overflow-hidden">
              <div className="px-6 py-4 bg-gradient-to-r from-slate-50 to-slate-100 border-b border-gray-200 shrink-0">
                <div className="flex items-start justify-between gap-4">
                  <div className="min-w-0">
                    <h3 className="text-lg font-bold text-main-dark truncate">{stepTitles[publishStep] || 'Publish'}</h3>
                    <div className="mt-2 flex items-center gap-2">
                      {steps.map((s, idx) => {
                        const active = publishStep === s.key;
                        const complete = steps.findIndex(st => st.key === publishStep) > idx;
                        return (
                          <div key={s.key} className="flex items-center gap-2">
                            <div className={`h-6 px-2 rounded-full text-[10px] font-bold uppercase tracking-wider ring-1 ${
                              active
                                ? 'bg-main-primary text-white ring-main-primary'
                                : complete
                                  ? 'bg-main-primary/10 text-main-primary ring-main-primary/20'
                                  : 'bg-white text-main-light ring-gray-200'
                            }`}>
                              <span className="inline-flex h-6 items-center">{idx + 1}. {s.label}</span>
                            </div>
                            {idx < steps.length - 1 && <div className="w-6 h-px bg-gray-300/70" />}
                          </div>
                        );
                      })}
                    </div>
                  </div>
                  <button
                    type="button"
                    onClick={handlePublishCancel}
                    disabled={isPublishing}
                    className="p-2 rounded-lg text-main-light hover:text-main-dark hover:bg-white/70 disabled:opacity-50"
                    title="Close"
                  >
                    <X className="w-5 h-5" />
                  </button>
                </div>
              </div>
              <div className="px-6 py-6 overflow-y-auto">
                {publishStep === 'choose' && (
                  <>
                    {sessionLabel && (
                      <div className="mb-4 p-3 rounded-xl bg-main-primary/5 border border-main-base">
                        <p className="text-[10px] font-black text-main-light uppercase tracking-widest mb-1">Publishing session</p>
                        <p className="text-sm font-semibold text-main-primary">{sessionLabel}</p>
                      </div>
                    )}
                    <p className="text-main-light text-sm mb-4">Choose which plan to publish to the active assignment.</p>
                    <div className="flex flex-col gap-2">
                      <button
                        type="button"
                        onClick={() => handlePublishChoice('planner')}
                        disabled={isPublishing}
                        className="px-4 py-3 text-left rounded-xl border border-main-base bg-white hover:bg-main-base-50 text-main-dark font-medium transition-colors disabled:opacity-50"
                      >
                        <span className="font-semibold">Planner plan</span>
                        <span className="block text-xs text-main-light mt-0.5">Current optimized result (drivers + unassigned)</span>
                      </button>
                      <button
                        type="button"
                        onClick={() => handlePublishChoice('portal')}
                        disabled={isPublishing}
                        className="px-4 py-3 text-left rounded-xl border border-main-base bg-white hover:bg-main-base-50 text-main-dark font-medium transition-colors disabled:opacity-50"
                      >
                        <span className="font-semibold">Portal plan</span>
                        <span className="block text-xs text-main-light mt-0.5">Original plan from the portal</span>
                      </button>
                    </div>
                  </>
                )}
                {publishStep === 'confirm' && (
                  <>
                    {sessionLabel && (
                      <div className="mb-4 p-3 rounded-xl bg-main-primary/5 border border-main-base">
                        <p className="text-[10px] font-black text-main-light uppercase tracking-widest mb-1">Publishing session</p>
                        <p className="text-sm font-semibold text-main-primary">{sessionLabel}</p>
                      </div>
                    )}
                    <p className="text-main-dark mb-2">
                      You are about to publish <strong>{publishChoice === 'portal' ? 'Portal plan' : 'Planner plan'}</strong>.
                    </p>
                    <p className="text-main-light text-sm">
                      {publishSelectionUnlocked
                        ? 'This will validate trips and let you select which drivers to include. Continue?'
                        : 'This will validate trips and publish the full plan. Continue?'}
                    </p>
                  </>
                )}
                {publishStep === 'select-drivers' && (
                  <>
                    {sessionLabel && (
                      <div className="mb-4 p-3 rounded-xl bg-main-primary/5 border border-main-base">
                        <p className="text-[10px] font-black text-main-light uppercase tracking-widest mb-1">Publishing session</p>
                        <p className="text-sm font-semibold text-main-primary">{sessionLabel}</p>
                      </div>
                    )}
                    <div className="mb-3 flex items-center justify-between">
                      <p className="text-main-light text-sm">
                        Select which drivers to include ({selectedDriverKeys.size}/{driverGroups.length} selected · {totalSelectedTrips} trips)
                      </p>
                      <button type="button" onClick={toggleAllDrivers} className="text-xs font-medium text-main-primary hover:underline shrink-0 ml-2">
                        {selectedDriverKeys.size === driverGroups.length ? 'Deselect all' : 'Select all'}
                      </button>
                    </div>
                    <div className="flex flex-col gap-1.5 max-h-64 overflow-y-auto">
                      {driverGroups.map((g) => (
                        <label
                          key={g.key}
                          className={`flex items-center gap-3 px-3 py-2.5 rounded-xl border cursor-pointer transition-colors ${
                            selectedDriverKeys.has(g.key)
                              ? 'border-main-primary/40 bg-main-primary/5'
                              : 'border-main-base bg-white hover:bg-main-base-50'
                          }`}
                        >
                          <input
                            type="checkbox"
                            checked={selectedDriverKeys.has(g.key)}
                            onChange={() => toggleDriverKey(g.key)}
                            className="h-4 w-4 rounded border-gray-300 text-main-primary focus:ring-main-primary shrink-0"
                          />
                          {g.profileImage ? (
                            <img src={g.profileImage} alt="" className="w-7 h-7 rounded-full object-cover shrink-0" />
                          ) : (
                            <div className={`w-7 h-7 rounded-full flex items-center justify-center text-xs font-bold shrink-0 ${
                              g.key === '__unassigned__' ? 'bg-gray-200 text-gray-500' : 'bg-main-primary/10 text-main-primary'
                            }`}>
                              {g.key === '__unassigned__' ? '?' : (g.name?.[0] || 'D').toUpperCase()}
                            </div>
                          )}
                          <div className="flex-1 min-w-0">
                            <span className="text-sm font-medium text-main-dark block truncate">{g.name}</span>
                            <span className="text-xs text-main-light">{g.tripCount} trip{g.tripCount !== 1 ? 's' : ''}</span>
                          </div>
                        </label>
                      ))}
                    </div>
                  </>
                )}
              </div>
              <div className="px-6 py-4 bg-main-base-50 border-t border-main-base flex justify-between shrink-0">
                <div>
                  {publishStep === 'confirm' && (
                    <button type="button" onClick={handleConfirmBack} disabled={isPublishing} className="inline-flex items-center gap-1 px-2 py-2 text-main-light hover:text-main-dark text-sm disabled:opacity-50">
                      <ArrowLeft className="w-3.5 h-3.5 shrink-0" />
                      Back
                    </button>
                  )}
                  {publishStep === 'select-drivers' && publishSelectionUnlocked && (
                    <button type="button" onClick={handleSelectDriversBack} disabled={isPublishing} className="inline-flex items-center gap-1 px-2 py-2 text-main-light hover:text-main-dark text-sm disabled:opacity-50">
                      <ArrowLeft className="w-3.5 h-3.5 shrink-0" />
                      Back
                    </button>
                  )}
                </div>
                <div className="flex space-x-3">
                  <button onClick={handlePublishCancel} disabled={isPublishing} className="btn-white disabled:opacity-50">Cancel</button>
                  {publishStep === 'confirm' && (
                    <button onClick={handlePublishConfirm} disabled={isPublishing} className="btn-primary disabled:opacity-50 flex items-center space-x-2">
                      {isPublishing && <div className="animate-spin rounded-full h-4 w-4 border-b-2 border-white" />}
                      <span>{isPublishing ? (publishSelectionUnlocked ? 'Validating...' : 'Publishing...') : (publishSelectionUnlocked ? 'Continue' : 'Validate & Publish')}</span>
                    </button>
                  )}
                  {publishStep === 'select-drivers' && publishSelectionUnlocked && (
                    <button onClick={handleFinalPublish} disabled={isPublishing || selectedDriverKeys.size === 0} className="btn-primary disabled:opacity-50 flex items-center space-x-2">
                      {isPublishing && <div className="animate-spin rounded-full h-4 w-4 border-b-2 border-white" />}
                      <span>{isPublishing ? 'Publishing...' : `Publish (${selectedDriverKeys.size})`}</span>
                    </button>
                  )}
                </div>
              </div>
            </div>
          </>
        );
      })()}
    </>
  );
};

export default TimeFilter;
